import {useCallback, useEffect, useState} from "react";
import PropTypes from "prop-types";
import {FormattedMessage} from "react-intl";
import messages from "./messages";
import {Close, Info} from "@material-ui/icons";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Popover,
  Typography,
} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import RefrezhButton from "components/RefrezhButton";
import ToggleButton from "../../components/ToggleButton/ToggleButton";
import {withRouter} from "react-router";
import CustomInputComponent from "../../components/CustomInputComponent/CustomInputComponent";
import clsx from "clsx";
import UploadStl from "components/UploadStl/UploadStl";

const OFF = 2;

const useStyles = makeStyles(theme => ({
  dialog: {
    position: "relative",
  },
  dialogHeader: {
    borderBottom: "1px solid rgba(0,0,0,0.23)",
  },
  dialogActions: {
    borderTop: "1px solid rgba(0,0,0,0.23)",
    padding: theme.spacing(2),
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  container: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2),
  },
  actionContainerLeft: {
    flexDirection: "column",
    display: "flex",
    borderRight: "1px solid rgba(0,0,0,0.23)",
    padding: theme.spacing(1),
    flex: 1,
  },
  popover: {
    pointerEvents: "none",
    width: "400px",
    padding: theme.spacing(1),
  },
  actionContainerRight: {
    flexDirection: "column",
    display: "flex",
    flex: 1,
    padding: theme.spacing(1),
  },
  title: {},
  field: {
    width: "100%",
  },
  toggleButtons: {
    width: "75%",
    marginTop: theme.spacing(2),
  },
  remakeBox: {
    borderBottom: "1px solid rgba(0,0,0,0.23)",
  },
  uploadContainer: {
    width: "100%",
    display: "flex",
    padding: theme.spacing(0, 2),

    "& > div": {
      flex: 1,
    },
  },
}));

const toggleOptions = [
  {
    value: 1,
    label: <FormattedMessage {...messages.remake_product_yes} />,
  },
  {
    value: 2,
    label: <FormattedMessage {...messages.remake_product_no} />,
  },
];

function RemakeProductOrderModal({
  isOpenRefitting = false,
  isOpenRemake = false,
  isOpenGuarantee = false,
  onClose,
  onRemakeRequest,
  hasError,
  dataCy,
  activeMessage,
  remakeProductOrder,
  history,
}) {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [upperProductRemake, setUpperProductRemake] = useState(1);
  const [lowerProductRemake, setLowerProductRemake] = useState(1);
  const [upperProductChange, setUpperProductChange] = useState(1);
  const [lowerProductChange, setLowerProductChange] = useState(1);
  const [documents, setDocuments] = useState([]);
  const [comment, setComment] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(
    () => {
      if (remakeProductOrder) {
        if (remakeProductOrder.status === 3) {
          history.push(
            `/order/patient/${activeMessage.patient.id}/treatment/${
              remakeProductOrder.id
            }`,
          );
        }
        onClose();
      }
    },
    [activeMessage.patient.id, history, onClose, remakeProductOrder],
  );

  const handlePopoverOpen = event => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const makeRequest = useCallback(
    () => {
      onRemakeRequest({
        practiceId: activeMessage.practiceId,
        productOrderId: activeMessage.productOrder.id,
        remakeRequest: {
          documents: documents.map(doc => ({ id: doc.id })),
          upperProductChange: upperProductChange === 1,
          upperProductRemake: upperProductRemake === 1,
          lowerProductChange: lowerProductChange === 1,
          lowerProductRemake: lowerProductRemake === 1,
          remake: !isOpenGuarantee,
          refitting: isOpenRefitting,
          comment,
        },
      });
    },
    [
      onRemakeRequest,
      activeMessage.practiceId,
      activeMessage.productOrder.id,
      documents,
      upperProductChange,
      upperProductRemake,
      lowerProductChange,
      lowerProductRemake,
      isOpenGuarantee,
      isOpenRefitting,
      comment,
    ],
  );

  return (
    <Dialog
      open={isOpenRefitting || isOpenGuarantee || isOpenRemake}
      onClose={onClose}
      data-cy={dataCy}
      maxWidth="md"
      fullWidth
      className={classes.dialog}
    >
      <DialogTitle className={classes.dialogHeader}>
        <div className={classes.header}>
          <div data-cy="headerRemakeModal">
            {isOpenGuarantee ? (
              <FormattedMessage {...messages.remake_product_title_warranty} />
            ) : (
              isOpenRefitting ? ( <FormattedMessage {...messages.refitting_remake_product_title} />
              ) : (
                  <FormattedMessage {...messages.remake_product_title} />
                )
            )}
          </div>
          <IconButton onClick={onClose} data-cy="headerCloseButton">
            <Close />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        <div className={classes.container} data-cy="descriptionRemake">
          <CustomInputComponent
            onChange={e => setComment(e.target.value)}
            value={comment}
            label={<FormattedMessage {...messages.remake_product_comment} />}
            className={classes.field}
            multiline
          />
          <div className={classes.uploadContainer}>
            <UploadStl
              onChange={docs => {
                setDocuments(docs);
              }}
              isAlternative
              fileUrl={`/practices/${activeMessage.practiceId}/document`}
            />
          </div>
        </div>
        <Divider />
        {(isOpenGuarantee || isOpenRemake) && (
          <div className={clsx(classes.container, classes.remakeBox)}>
            <FormattedMessage {...messages.remake_product_title} />
            <Info
              fontSize="small"
              aria-haspopup="true"
              onMouseEnter={handlePopoverOpen}
              onMouseLeave={handlePopoverClose}
              data-cy="infoIcon"
            />
          </div>
        )}
        <div className={classes.container}>
          {/* upper */}
          {(() => {
            if(activeMessage.productOrder.upperProductName != null){
              return <div className={classes.actionContainerLeft}>
                <Typography variant="subtitle1">
                  <FormattedMessage {...messages.remake_product_upper_jaw} />
                </Typography>
                <Typography className={classes.title} variant="h6">
                  {activeMessage.productOrder.upperProductName}
                </Typography>
                <ToggleButton
                  key="1"
                  className={classes.toggleButtons}
                  label={<FormattedMessage {...messages.remake_product_remake} />}
                  value={upperProductRemake}
                  borderColor="rgba(0,0,0,0.23)"
                  activeBorderColor="rgba(0,0,0,0.23)"
                  onChange={({ value }) => {
                    setLoading(false);
                    setUpperProductRemake(value);
                    if (value === OFF) setUpperProductChange(value);
                  }}
                  options={toggleOptions}
                  hasError={hasError}
                  dataCy="upperProductRemakeButton"
                />
                {!isOpenRefitting && (
                  <ToggleButton
                    key="2"
                    label={
                      <FormattedMessage {...messages.remake_product_make_changes} />
                    }
                    className={classes.toggleButtons}
                    value={upperProductChange}
                    borderColor="rgba(0,0,0,0.23)"
                    activeBorderColor="rgba(0,0,0,0.23)"
                    onChange={({ value }) => {
                      setLoading(false);
                      setUpperProductChange(value);
                    }}
                    options={toggleOptions}
                    hasError={hasError}
                    disabled={upperProductRemake === OFF}
                    dataCy="upperProductChangeButton"
                  />
                )}
              </div>
            }
          })()}

          {/* lower */}
          {(() => {
            if(activeMessage.productOrder.lowerProductName != null){
              return <div className={classes.actionContainerRight}>
                <FormattedMessage {...messages.remake_product_lower_jaw} />
                <Typography className={classes.title} variant="h6">
                  {activeMessage.productOrder.lowerProductName}
                </Typography>
                <ToggleButton
                  key="3"
                  className={classes.toggleButtons}
                  label={<FormattedMessage {...messages.remake_product_remake} />}
                  value={lowerProductRemake}
                  borderColor="rgba(0,0,0,0.23)"
                  activeBorderColor="rgba(0,0,0,0.23)"
                  onChange={({ value }) => {
                    setLoading(false);
                    setLowerProductRemake(value);
                    if (value === OFF) setLowerProductChange(value);
                  }}
                  options={toggleOptions}
                  hasError={hasError}
                  dataCy="lowerProductRemakeButton"
                />
                {!isOpenRefitting && (
                  <ToggleButton
                    key="4"
                    className={classes.toggleButtons}
                    label={
                      <FormattedMessage {...messages.remake_product_make_changes} />
                    }
                    value={lowerProductChange}
                    borderColor="rgba(0,0,0,0.23)"
                    activeBorderColor="rgba(0,0,0,0.23)"
                    onChange={({ value }) => {
                      setLoading(false);
                      setLowerProductChange(value);
                    }}
                    options={toggleOptions}
                    hasError={hasError}
                    disabled={lowerProductRemake === OFF}
                    dataCy="lowerProductChangeButton"
                  />
                )}
              </div>
            }
          })()} 
        </div>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <div data-cy="confirmButton">
          <RefrezhButton
            onClick={() => {
              setLoading(true);
              makeRequest();
            }}
            disabled={
              (upperProductRemake === OFF && lowerProductRemake === OFF) ||
              loading
            }
          >
            <FormattedMessage {...messages.remake_product_confirm} />
          </RefrezhButton>
        </div>
        <div data-cy="cancelButton">
          <RefrezhButton onClick={onClose}>
            <FormattedMessage {...messages.delete_patient_cancel} />
          </RefrezhButton>
        </div>
      </DialogActions>
      <Popover
        open={open}
        anchorEl={anchorEl}
        className={classes.popover}
        classes={{ paper: classes.popover }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={handlePopoverClose}
        disableRestoreFocus
        data-cy="popupMessage"
      >
        <Typography variant="caption">
          {isOpenGuarantee && (
            <FormattedMessage {...messages.warranty_popover_info} />
          )}
          {isOpenRemake && (
            <FormattedMessage {...messages.remake_popover_info} />
          )}
        </Typography>
      </Popover>
    </Dialog>
  );
}

RemakeProductOrderModal.propTypes = {
  isOpenGuarantee: PropTypes.bool,
  isOpenRemake: PropTypes.bool,
  isOpenRefitting: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  activeMessage: PropTypes.object,
  onRemakeRequest: PropTypes.func,
};

export default withRouter(RemakeProductOrderModal);
